import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    InputAdornment,
    IconButton,
    Alert,
    Divider,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material'
import {
    Visibility,
    VisibilityOff,
    Email,
    Lock,
    Person,
    Phone,
    Pin,
    CheckCircle,
    Language,
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { endUserApi } from '@api/endUser'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { useTranslation } from '@i18n'
import { toast } from 'react-toastify'

const EndUserLogin = () => {
    const { t, language, setLanguage } = useTranslation()
    const navigate = useNavigate()
    const { login: setAuth } = useAuthStore()
    const { themeSettings } = useUIStore()
    const [showPassword, setShowPassword] = useState(false)
    const [isRegister, setIsRegister] = useState(false)

    // Registration steps: 0 = enter email, 1 = verify code, 2 = complete registration
    const [registerStep, setRegisterStep] = useState(0)
    const [verifiedEmail, setVerifiedEmail] = useState('')
    const [verificationCode, setVerificationCode] = useState('')

    const isRTL = language === 'ar'

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        getValues,
        watch,
    } = useForm({
        defaultValues: {
            email: '',
            password: '',
            full_name: '',
            phone: '',
            code: '',
        },
    })

    const emailValue = watch('email')

    // Login mutation
    const loginMutation = useMutation({
        mutationFn: ({ email, password }) => endUserApi.login(email, password),
        onSuccess: (response) => {
            const { token, user } = response.data
            setAuth(token, user)
            toast.success(isRTL ? 'تم تسجيل الدخول بنجاح' : 'Login successful')
            navigate('/portal/dashboard')
        },
        onError: (error) => {
            const message = error.response?.data?.message || (isRTL ? 'فشل تسجيل الدخول' : 'Login failed')
            toast.error(message)
        },
    })

    // Send verification code mutation
    const sendCodeMutation = useMutation({
        mutationFn: ({ email }) => endUserApi.sendVerificationCode(email, language),
        onSuccess: () => {
            setVerifiedEmail(getValues('email'))
            setRegisterStep(1)
            toast.success(isRTL ? 'تم إرسال رمز التحقق إلى بريدك الإلكتروني' : 'Verification code sent to your email')
        },
        onError: (error) => {
            const message = error.response?.data?.message || (isRTL ? 'فشل إرسال الرمز' : 'Failed to send code')
            toast.error(message)
        },
    })

    // Verify code mutation
    const verifyCodeMutation = useMutation({
        mutationFn: ({ email, code }) => endUserApi.verifyCode(email, code),
        onSuccess: (_, variables) => {
            setVerificationCode(variables.code)
            setRegisterStep(2)
            toast.success(isRTL ? 'تم التحقق من البريد الإلكتروني' : 'Email verified successfully')
        },
        onError: (error) => {
            const message = error.response?.data?.message || (isRTL ? 'رمز التحقق غير صحيح' : 'Invalid verification code')
            toast.error(message)
        },
    })

    // Register mutation
    const registerMutation = useMutation({
        mutationFn: ({ full_name, password, phone }) =>
            endUserApi.register(full_name, verifiedEmail, password, verificationCode, phone),
        onSuccess: () => {
            toast.success(isRTL ? 'تم إنشاء الحساب بنجاح. يمكنك الآن تسجيل الدخول.' : 'Account created successfully. You can now login.')
            setIsRegister(false)
            setRegisterStep(0)
            setVerifiedEmail('')
            setVerificationCode('')
            reset()
        },
        onError: (error) => {
            const message = error.response?.data?.message || (isRTL ? 'فشل التسجيل' : 'Registration failed')
            toast.error(message)
        },
    })

    const onSubmit = (data) => {
        if (!isRegister) {
            // Login
            loginMutation.mutate(data)
        } else if (registerStep === 0) {
            // Step 1: Send code
            sendCodeMutation.mutate({ email: data.email })
        } else if (registerStep === 1) {
            // Step 2: Verify code
            verifyCodeMutation.mutate({ email: verifiedEmail, code: data.code })
        } else {
            // Step 3: Complete registration
            registerMutation.mutate(data)
        }
    }

    const isLoading = loginMutation.isPending || sendCodeMutation.isPending ||
        verifyCodeMutation.isPending || registerMutation.isPending

    const handleResendCode = () => {
        sendCodeMutation.mutate({ email: verifiedEmail })
    }

    const steps = isRTL
        ? ['البريد الإلكتروني', 'التحقق', 'إكمال التسجيل']
        : ['Email', 'Verify', 'Complete']

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1a3a6b 0%, #0d2247 100%)',
                p: 2,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 450,
                    borderRadius: 3,
                }}
            >
                {/* Language Switcher */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                        size="small"
                        startIcon={<Language />}
                        onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                        {language === 'ar' ? 'English' : 'العربية'}
                    </Button>
                </Box>

                {/* Logo/Header */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        component="img"
                        src={themeSettings?.logoUrl || '/moj-logo.png'}
                        alt="Logo"
                        sx={{
                            width: 80,
                            height: 'auto',
                            mx: 'auto',
                            mb: 2,
                        }}
                    />
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        {isRegister
                            ? (isRTL ? 'إنشاء حساب جديد' : 'Create Account')
                            : (isRTL ? 'بوابة المستخدم' : 'User Portal')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isRegister
                            ? (isRTL ? 'أدخل بياناتك لإنشاء حساب' : 'Enter your details to create an account')
                            : (isRTL ? 'سجل الدخول لمتابعة طلباتك' : 'Sign in to track your submissions')}
                    </Typography>
                </Box>

                {/* Registration Steps */}
                {isRegister && (
                    <Stepper
                        activeStep={registerStep}
                        sx={{
                            mb: 3,
                            direction: isRTL ? 'rtl' : 'ltr',
                            '& .MuiStepConnector-line': {
                                borderColor: 'rgba(0,0,0,0.12)',
                            },
                        }}
                        alternativeLabel
                    >
                        {(isRTL ? [...steps].reverse() : steps).map((label, index) => {
                            const actualStep = isRTL ? steps.length - 1 - index : index;
                            return (
                                <Step key={label} completed={registerStep > actualStep}>
                                    <StepLabel
                                        StepIconProps={{
                                            sx: {
                                                color: registerStep >= actualStep ? 'primary.main' : 'grey.400',
                                            }
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* LOGIN FORM */}
                    {!isRegister && (
                        <>
                            <TextField
                                fullWidth
                                label={isRTL ? 'البريد الإلكتروني' : 'Email'}
                                type="email"
                                margin="normal"
                                {...register('email', {
                                    required: isRTL ? 'البريد الإلكتروني مطلوب' : 'Email is required',
                                })}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label={isRTL ? 'كلمة المرور' : 'Password'}
                                type={showPassword ? 'text' : 'password'}
                                margin="normal"
                                {...register('password', {
                                    required: isRTL ? 'كلمة المرور مطلوبة' : 'Password is required',
                                })}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </>
                    )}

                    {/* REGISTRATION STEP 0: Enter Email */}
                    {isRegister && registerStep === 0 && (
                        <TextField
                            fullWidth
                            label={isRTL ? 'البريد الإلكتروني' : 'Email'}
                            type="email"
                            margin="normal"
                            {...register('email', {
                                required: isRTL ? 'البريد الإلكتروني مطلوب' : 'Email is required',
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: isRTL ? 'صيغة البريد غير صحيحة' : 'Invalid email format',
                                },
                            })}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    {/* REGISTRATION STEP 1: Enter Verification Code */}
                    {isRegister && registerStep === 1 && (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {isRTL
                                    ? `تم إرسال رمز التحقق إلى ${verifiedEmail}`
                                    : `A verification code was sent to ${verifiedEmail}`}
                            </Alert>
                            <TextField
                                fullWidth
                                label={isRTL ? 'رمز التحقق' : 'Verification Code'}
                                margin="normal"
                                {...register('code', {
                                    required: isRTL ? 'الرمز مطلوب' : 'Code is required',
                                    minLength: { value: 6, message: isRTL ? '6 أرقام' : '6 digits required' },
                                    maxLength: { value: 6, message: isRTL ? '6 أرقام' : '6 digits required' },
                                })}
                                error={!!errors.code}
                                helperText={errors.code?.message}
                                inputProps={{ maxLength: 6, style: { letterSpacing: '8px', textAlign: 'center', fontSize: '1.5rem' } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Pin color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                variant="text"
                                size="small"
                                onClick={handleResendCode}
                                disabled={sendCodeMutation.isPending}
                                sx={{ mt: 1 }}
                            >
                                {isRTL ? 'إعادة إرسال الرمز' : 'Resend Code'}
                            </Button>
                        </>
                    )}

                    {/* REGISTRATION STEP 2: Complete Profile */}
                    {isRegister && registerStep === 2 && (
                        <>
                            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                                {isRTL ? 'تم التحقق من بريدك الإلكتروني!' : 'Email verified!'}
                            </Alert>
                            <TextField
                                fullWidth
                                label={isRTL ? 'الاسم الكامل' : 'Full Name'}
                                margin="normal"
                                {...register('full_name', { required: isRTL ? 'الاسم مطلوب' : 'Name is required' })}
                                error={!!errors.full_name}
                                helperText={errors.full_name?.message}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label={isRTL ? 'رقم الهاتف' : 'Phone Number'}
                                margin="normal"
                                {...register('phone')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                label={isRTL ? 'كلمة المرور' : 'Password'}
                                type={showPassword ? 'text' : 'password'}
                                margin="normal"
                                {...register('password', {
                                    required: isRTL ? 'كلمة المرور مطلوبة' : 'Password is required',
                                    minLength: { value: 6, message: isRTL ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters' }
                                })}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        sx={{
                            mt: 3,
                            mb: 2,
                            py: 1.5,
                            background: 'linear-gradient(135deg, #1a3a6b 0%, #c9a227 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #256b56 0%, #3d9477 100%)',
                            },
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : !isRegister ? (
                            isRTL ? 'تسجيل الدخول' : 'Sign In'
                        ) : registerStep === 0 ? (
                            isRTL ? 'إرسال رمز التحقق' : 'Send Verification Code'
                        ) : registerStep === 1 ? (
                            isRTL ? 'تحقق من الرمز' : 'Verify Code'
                        ) : (
                            isRTL ? 'إنشاء الحساب' : 'Create Account'
                        )}
                    </Button>
                </form>

                <Divider sx={{ my: 2 }} />

                {/* Toggle Login/Register */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {isRegister
                            ? (isRTL ? 'لديك حساب بالفعل؟' : 'Already have an account?')
                            : (isRTL ? 'ليس لديك حساب؟' : "Don't have an account?")}
                        {' '}
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                                setIsRegister(!isRegister)
                                setRegisterStep(0)
                                setVerifiedEmail('')
                                setVerificationCode('')
                                reset()
                            }}
                        >
                            {isRegister
                                ? (isRTL ? 'تسجيل الدخول' : 'Sign In')
                                : (isRTL ? 'إنشاء حساب' : 'Create Account')}
                        </Button>
                    </Typography>
                </Box>
            </Paper>
        </Box>
    )
}

export default EndUserLogin
